/**
 * Compatibility shim for react-redux-i18n → react-i18next migration.
 *
 * I18n, Translate, and Localize are all backed by i18next.
 */
import React from "react"
import moment from "moment"
import { useTranslation } from "react-i18next"
import i18next from "../i18n"

// --- I18n ---

const missingKeyFallback = (key) => {
  if (!key) return null
  if (key.startsWith("errors.")) return key.slice("errors.".length)
  return key.slice(key.lastIndexOf(".") + 1)
}

export const I18n = {
  t: (key, options) => {
    if (!key) return null
    // react-redux-i18n took the language as `locale`; i18next calls it `lng`
    const { locale, ...rest } = options ?? {}
    const result = i18next.t(key, {
      returnObjects: true,
      ...(locale ? { lng: locale } : {}),
      ...rest
    })
    // i18next returns the key itself when missing
    return result === key ? missingKeyFallback(key) : result
  },
  _getTranslations: () => {
    // Return flat {locale: {key: value}} shape
    const store = i18next.store?.data ?? {}
    const result = {}
    for (const [lng, namespaces] of Object.entries(store)) {
      result[lng] = namespaces?.translation ?? {}
    }
    return result
  },
  // no-op: missing key handling is built into .t() above
  setHandleMissingTranslation: () => {},
  l: (value, options = {}) => {
    if (options.dateFormat) {
      const format = i18next.t(options.dateFormat)
      return moment(value).format(format)
    }
    if (options.style === "currency") {
      return new Intl.NumberFormat(i18next.language, options).format(value)
    }
    return new Intl.NumberFormat(i18next.language, options).format(value)
  }
}

// --- Translate ---

/**
 * @param {{ value: string, className?: string, tag?: string, dangerousHTML?: boolean, [key: string]: any }} props
 */
export const Translate = ({
  value,
  className = null,
  tag: Tag = "span",
  dangerousHTML = false,
  ...rest
}) => {
  const { t } = useTranslation()
  const result = t(value, { returnObjects: true, ...rest })
  const translated = result === value ? missingKeyFallback(value) : result
  if (dangerousHTML) {
    return (
      <Tag
        className={className}
        dangerouslySetInnerHTML={{ __html: translated }}
      />
    )
  }
  return <Tag className={className}>{translated}</Tag>
}

// Some files import Translate as BaseTranslate — same component
export { Translate as BaseTranslate }

// --- Localize ---
/**
 * @param {{ value: any, dateFormat?: string, style?: string, [key: string]: any }} props
 */
export const Localize = ({ value, ...options }) => {
  useTranslation() // re-render with the new format when the language switches
  return <>{I18n.l(value, options)}</>
}
